import { Text } from "@blockdaemon-internal/precision-ui";
import { Trans } from "@lingui/react/macro";
import {
  Accordion,
  Alert,
  Box,
  Button,
  Code,
  Container,
  Divider,
  Group,
  Space,
  Stack,
  Title,
  useMantineColorScheme,
} from "@mantine/core";
import { useState } from "react";
import badgePurple from "../../assets/blockdeamon-badge-purple.svg";
import badgeWhite from "../../assets/blockdeamon-badge-white.svg";

interface AppError extends Error {
  code: string;
  details?: Record<string, unknown>;
  timestamp?: string;
}

interface ErrorFallbackProps {
  error: unknown;
  resetError?: () => void;
  eventId?: string;
  source?: "global" | "router" | "widget";
}

export function ErrorFallback({
  error,
  resetError,
  eventId,
  source = "global",
}: ErrorFallbackProps) {
  const [showDetails, setShowDetails] = useState(false);
  const { colorScheme } = useMantineColorScheme();

  const handleRefresh = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  const errorMessage = error instanceof Error ? error.message : String(error);
  const isAppError = error instanceof Error && "code" in error;
  const appError = isAppError ? (error as AppError) : null;

  return (
    <Container
      display="flex"
      style={{ alignItems: "center", justifyContent: "center" }}
      h={source === "global" ? "100vh" : "auto"}
    >
      <Container size="md" py="xl">
        <Stack align="center" spacing={0}>
          {/* Header Section - Logo, Title, Description */}
          <Stack align="center" spacing="md" mb="2rem">
            <Box>
              {colorScheme === "dark" ? (
                <Box
                  component="img"
                  src={badgeWhite}
                  alt={<Trans>Blockdeamon Badge White</Trans>}
                  w={60}
                  h={60}
                />
              ) : (
                <Box
                  component="img"
                  src={badgePurple}
                  alt={<Trans>Blockdeamon Badge Purple</Trans>}
                  w={60}
                  h={60}
                />
              )}
            </Box>

            <Stack align="center" spacing="xs" maw={480}>
              <Title order={1} ta="center" size="2rem" fw={600}>
                <Trans>Something went wrong</Trans>
              </Title>

              <Text
                ta="center"
                c="gray.9"
                variant="body"
                sx={{
                  lineHeight: 1.5,
                }}
              >
                <Trans>
                  We encountered an unexpected error.
                  <br />
                  Our team has been automatically notified.
                </Trans>
              </Text>
            </Stack>
          </Stack>

          {/* Error Details Section */}
          {appError && (
            <Container size="sm" mb="2rem">
              <Alert color="red" title={<Trans>Error Details</Trans>} w="100%">
                <Stack spacing="xs">
                  <Text size="sm">
                    <strong>
                      <Trans>Code:</Trans>
                    </strong>{" "}
                    {appError.code}
                  </Text>
                  <Text size="sm">{appError.message}</Text>
                </Stack>
              </Alert>
            </Container>
          )}

          {/* Actions Section */}
          <Stack align="center" spacing="xl" w="100%" maw={400}>
            {/* Primary Actions */}
            <Group position="center" spacing="lg" w="100%">
              <Button onClick={handleRefresh} size="md" style={{ minWidth: "120px" }}>
                <Trans>Try Again</Trans>
              </Button>
              <Button
                variant="secondary"
                onClick={() => window.location.reload()}
                size="md"
                style={{ minWidth: "120px" }}
              >
                <Trans>Reload Page</Trans>
              </Button>
            </Group>

            {/* Divider */}
            <Divider
              w="60%"
              sx={(theme) => ({
                borderColor:
                  theme.colorScheme === "dark" ? theme.colors.gray[6] : theme.colors.gray[3],
              })}
            />

            {/* Secondary Actions */}
            <Group position="center" spacing="md">
              <Button size="sm" onClick={() => setShowDetails(!showDetails)}>
                {showDetails ? <Trans>Hide</Trans> : <Trans>Show</Trans>}{" "}
                <Trans>Technical Details</Trans>
              </Button>
            </Group>
          </Stack>

          {/* Technical Details Section */}
          {showDetails && (
            <>
              <Space h="xl" />
              <Container size="sm" w="100%">
                {eventId ? (
                  /* Sentry Error Display */
                  <Stack spacing="md" align="center">
                    <Text size="sm" fw={500} ta="center" c="gray.4">
                      <Trans>Technical Information</Trans>
                    </Text>
                    <Code
                      block
                      c="red"
                      fz="xs"
                      p="lg"
                      w="100%"
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        maxHeight: "200px",
                        overflow: "auto",
                      }}
                    >
                      {errorMessage}
                    </Code>
                    <Text size="xs" color="gray.4" ta="center" fw={500}>
                      <Trans>Error ID:</Trans>{" "}
                      <Box component="span" ff="monospace">
                        {eventId}
                      </Box>
                    </Text>
                  </Stack>
                ) : (
                  /* Router Error Display */
                  <Accordion variant="contained" w="100%">
                    <Accordion.Item value="error-details">
                      <Accordion.Control>
                        <Text size="sm">
                          <Trans>Technical Details</Trans>
                        </Text>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Code
                          block
                          c="red"
                          fz="xs"
                          p="md"
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            maxHeight: "200px",
                            overflow: "auto",
                          }}
                        >
                          {errorMessage}
                        </Code>
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>
                )}
              </Container>
            </>
          )}

          {/* Footer Section */}
          <Space h="2rem" />
          <Container size="sm">
            <Text
              variant="body"
              c="gray.9"
              ta="center"
              size="sm"
              sx={{
                lineHeight: 1.4,
              }}
            >
              <Trans>
                If this problem persists, please contact support
                <br />
                and reference the error details above.
              </Trans>
            </Text>
          </Container>
        </Stack>
      </Container>
    </Container>
  );
}
